/**
 * UserProfile.js
 *
 * Individual user profile page showing a unified timeline of all activities.
 * Displays gifts given, gifts received, and gift pools created in chronological order.
 * Respects privacy settings - private profiles show limited information.
 *
 * Route: /profile/:userId
 */

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { SERVER_URL } from './config';
import { useAuth } from './AuthContext';
import Navigation from './components/Navigation';
import GiftPoolPost from './components/GiftPoolPost';
import Avatar from './components/Avatar';
import ProfilePhotoEditor from './components/ProfilePhotoEditor';
import { addComment, loadComments, deleteComment, editComment } from './utils/commentUtils';
import { uploadImages } from './utils/uploadImages';
import SortableImageGrid from './components/SortableImageGrid';
import { CATEGORIES } from './utils/constants';

function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followStatusLoaded, setFollowStatusLoaded] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFollowing, setLoadingFollowing] = useState(false);
  const [expandedDonors, setExpandedDonors] = useState(new Set());
  const [donors, setDonors] = useState({});
  const [expandedLikes, setExpandedLikes] = useState(new Set());
  const [likers, setLikers] = useState({});
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [replyingTo, setReplyingTo] = useState({});
  const [replyText, setReplyText] = useState({});
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [vouchedPosts, setVouchedPosts] = useState(new Set());
  const [commentedPosts, setCommentedPosts] = useState(new Set());
  const [expandedVouches, setExpandedVouches] = useState(new Set());
  const [vouchers, setVouchers] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    show_gift_stats: true, // Show/hide the gift stats cards
    show_gift_amounts: true, // Show amounts in activity feed or just names
  });
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  // Unified image list for edit modal: {type:'existing'|'new', id?, image_url?, file?, preview}
  const [editImages, setEditImages] = useState([]);
  const [editImagesToDelete, setEditImagesToDelete] = useState([]);
  const [editImageError, setEditImageError] = useState('');
  const [activeTab, setActiveTab] = useState('pools');

  useEffect(() => {
    // Build URL with optional current_user_email parameter for privacy filtering
    const url = new URL(`${SERVER_URL}/users/${userId}`);
    if (isAuthenticated && user && user.email) {
      url.searchParams.append('current_user_email', user.email);
    }

    fetch(url.toString())
      .then(res => {
        if (!res.ok) throw new Error('User not found');
        return res.json();
      })
      .then(data => {
        setProfile(data);
        // Load privacy settings if available
        if (data.show_gift_stats !== undefined) {
          setPrivacySettings({
            show_gift_stats: data.show_gift_stats ?? true,
            show_gift_amounts: data.show_gift_amounts ?? true,
          });
        }

        // Check if current user is following this profile
        if (isAuthenticated && user && user.id !== userId) {
          fetch(`${SERVER_URL}/users/${user.id}/is-following/${userId}`)
            .then(res => res.json())
            .then(data => {
              setIsFollowing(data.is_following);
              setFollowStatusLoaded(true);
            })
            .catch(err => {
              console.error('Error checking follow status:', err);
              setFollowStatusLoaded(true);
            });
        } else {
          setFollowStatusLoaded(true);
        }

        return data;
      })
      .then(data => {
        // After profile loads, check which posts the current user has liked/vouched
        if (isAuthenticated && user && data && data.posts) {
          // Load liked posts
          Promise.all(
            data.posts.map(post =>
              fetch(
                `${SERVER_URL}/posts/${post.id}/liked?user_email=${encodeURIComponent(user.email)}`
              )
                .then(res => res.json())
                .then(result => ({ postId: post.id, liked: result.liked }))
                .catch(() => ({ postId: post.id, liked: false }))
            )
          ).then(likedResults => {
            const liked = new Set(likedResults.filter(r => r.liked).map(r => r.postId));
            setLikedPosts(liked);
          });

          // Load vouched posts
          Promise.all(
            data.posts.map(post =>
              fetch(
                `${SERVER_URL}/posts/${post.id}/vouched?user_email=${encodeURIComponent(user.email)}`
              )
                .then(res => res.json())
                .then(result => ({ postId: post.id, vouched: result.vouched }))
                .catch(() => ({ postId: post.id, vouched: false }))
            )
          ).then(vouchedResults => {
            const vouched = new Set(vouchedResults.filter(r => r.vouched).map(r => r.postId));
            setVouchedPosts(vouched);
          });

          // Load commented posts
          Promise.all(
            data.posts.map(post =>
              fetch(
                `${SERVER_URL}/posts/${post.id}/commented?user_email=${encodeURIComponent(user.email)}`
              )
                .then(res => res.json())
                .then(result => ({ postId: post.id, commented: result.commented }))
                .catch(() => ({ postId: post.id, commented: false }))
            )
          ).then(commentedResults => {
            const commented = new Set(commentedResults.filter(r => r.commented).map(r => r.postId));
            setCommentedPosts(commented);
          });
        }
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });

    // Set loading to false after a brief delay to ensure all data is loaded
    // This prevents the hash handling effect from interfering
    const loadingTimer = setTimeout(() => {
      setLoading(false);
    }, 100);

    return () => clearTimeout(loadingTimer);
  }, [userId, isAuthenticated, user]);

  // Handle highlighting post from URL hash - runs after profile loads
  useEffect(() => {
    if (!profile || !profile.posts || loading) return;

    // Track the last processed hash to avoid re-triggering
    let lastProcessedHash = '';

    const handleHashChange = () => {
      const hash = window.location.hash;

      // Skip if we've already processed this hash
      if (hash === lastProcessedHash) {
        return;
      }

      if (hash.startsWith('#post-')) {
        const postId = hash.replace('#post-', '').trim();

        // Mark this hash as processed
        lastProcessedHash = hash;
        setHighlightedPostId(postId);

        // Scroll to post with a small delay to ensure DOM is fully rendered
        // This is especially important on page refresh
        setTimeout(() => {
          const element = document.getElementById(`post-${postId}`);
          if (element) {
            // Get element position and scroll with offset for navigation bar
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 120; // 120px offset for nav bar

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            });

            // Remove highlight after 2 seconds
            setTimeout(() => {
              setHighlightedPostId(null);
            }, 2000);
          }
        }, 200); // 200ms delay to ensure DOM is ready
      } else {
        // Clear last processed hash if hash is removed
        lastProcessedHash = '';
      }
    };

    // Handle initial hash on load
    handleHashChange();

    // Listen for hash changes (when clicking notifications while already on profile)
    window.addEventListener('hashchange', handleHashChange);

    // Also check periodically for hash changes (for when navigate() doesn't fire hashchange)
    // This ensures we catch hash changes even when React Router doesn't fire hashchange
    const checkHashInterval = setInterval(() => {
      handleHashChange();
    }, 100);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      clearInterval(checkHashInterval);
    };
  }, [profile, profile?.posts, loading]);

  const handleFollowToggle = async () => {
    if (!isAuthenticated || !user) {
      alert('Please log in to follow users');
      return;
    }

    setFollowLoading(true);
    try {
      const url = `${SERVER_URL}/users/${userId}/follow?follower_email=${encodeURIComponent(user.email)}`;
      const method = isFollowing ? 'DELETE' : 'POST';

      const response = await fetch(url, { method });

      if (!response.ok) {
        let errorMessage = 'Failed to update follow status';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      setIsFollowing(!isFollowing);

      // Update follower count in profile
      setProfile(prev => ({
        ...prev,
        followers_count: isFollowing ? prev.followers_count - 1 : prev.followers_count + 1,
      }));
    } catch (err) {
      console.error('Error toggling follow:', err);
      alert(typeof err === 'string' ? err : err.message || 'Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShowFollowers = async () => {
    setShowFollowersModal(true);
    setLoadingFollowers(true);
    try {
      const response = await fetch(`${SERVER_URL}/users/${userId}/followers`);
      const data = await response.json();
      setFollowersList(data.followers || []);
    } catch (err) {
      console.error('Error loading followers:', err);
    } finally {
      setLoadingFollowers(false);
    }
  };

  const handleShowFollowing = async () => {
    setShowFollowingModal(true);
    setLoadingFollowing(true);
    try {
      const response = await fetch(`${SERVER_URL}/users/${userId}/following`);
      const data = await response.json();
      setFollowingList(data.following || []);
    } catch (err) {
      console.error('Error loading following:', err);
    } finally {
      setLoadingFollowing(false);
    }
  };

  const toggleDonors = async postId => {
    const isExpanded = expandedDonors.has(postId);

    if (isExpanded) {
      const newExpanded = new Set(expandedDonors);
      newExpanded.delete(postId);
      setExpandedDonors(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedComments = new Set(expandedComments);
      newExpandedComments.delete(postId);
      setExpandedComments(newExpandedComments);

      const newExpandedLikes = new Set(expandedLikes);
      newExpandedLikes.delete(postId);
      setExpandedLikes(newExpandedLikes);

      const newExpanded = new Set(expandedDonors);
      newExpanded.add(postId);
      setExpandedDonors(newExpanded);

      if (!donors[postId]) {
        try {
          const response = await fetch(`${SERVER_URL}/posts/${postId}/donors`);
          if (response.ok) {
            const data = await response.json();
            setDonors({ ...donors, [postId]: data.donors });
          }
        } catch (err) {
          console.error('Error loading donors:', err);
        }
      }
    }
  };

  const toggleLikes = async postId => {
    const isExpanded = expandedLikes.has(postId);

    if (isExpanded) {
      const newExpanded = new Set(expandedLikes);
      newExpanded.delete(postId);
      setExpandedLikes(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedComments = new Set(expandedComments);
      newExpandedComments.delete(postId);
      setExpandedComments(newExpandedComments);

      const newExpandedDonors = new Set(expandedDonors);
      newExpandedDonors.delete(postId);
      setExpandedDonors(newExpandedDonors);

      const newExpanded = new Set(expandedLikes);
      newExpanded.add(postId);
      setExpandedLikes(newExpanded);

      if (!likers[postId]) {
        try {
          const response = await fetch(`${SERVER_URL}/posts/${postId}/likers`);
          if (response.ok) {
            const data = await response.json();
            setLikers({ ...likers, [postId]: data.likers });
          }
        } catch (err) {
          console.error('Error loading likers:', err);
        }
      }
    }
  };

  const toggleComments = async postId => {
    const isExpanded = expandedComments.has(postId);

    if (isExpanded) {
      const newExpanded = new Set(expandedComments);
      newExpanded.delete(postId);
      setExpandedComments(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedDonors = new Set(expandedDonors);
      newExpandedDonors.delete(postId);
      setExpandedDonors(newExpandedDonors);

      const newExpandedLikes = new Set(expandedLikes);
      newExpandedLikes.delete(postId);
      setExpandedLikes(newExpandedLikes);

      const newExpanded = new Set(expandedComments);
      newExpanded.add(postId);
      setExpandedComments(newExpanded);

      if (!comments[postId]) {
        await loadComments(postId, comments, setComments);
      }
    }
  };

  const toggleVouches = async postId => {
    const isExpanded = expandedVouches.has(postId);

    if (isExpanded) {
      const newExpanded = new Set(expandedVouches);
      newExpanded.delete(postId);
      setExpandedVouches(newExpanded);
    } else {
      const newExpanded = new Set(expandedVouches);
      newExpanded.add(postId);
      setExpandedVouches(newExpanded);

      if (!vouchers[postId]) {
        try {
          const response = await fetch(`${SERVER_URL}/posts/${postId}/vouchers`);
          if (response.ok) {
            const data = await response.json();
            setVouchers({ ...vouchers, [postId]: data.vouchers });
          }
        } catch (err) {
          console.error('Error loading vouchers:', err);
        }
      }
    }
  };

  const handleLike = async post => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    const isLiked = likedPosts.has(post.id);
    const newLikedPosts = new Set(likedPosts);

    if (isLiked) {
      newLikedPosts.delete(post.id);
    } else {
      newLikedPosts.add(post.id);
    }
    setLikedPosts(newLikedPosts);

    try {
      const url = `${SERVER_URL}/posts/${post.id}/like?user_email=${encodeURIComponent(user.email)}`;
      const response = await fetch(url, {
        method: isLiked ? 'DELETE' : 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update like');
      }

      const data = await response.json();

      // Update like count in profile
      setProfile(prev => ({
        ...prev,
        posts: prev.posts.map(p => (p.id === post.id ? { ...p, like_count: data.like_count } : p)),
      }));

      // Reload likers list if it's currently expanded
      if (likers[post.id] || expandedLikes.has(post.id)) {
        try {
          const likersResponse = await fetch(`${SERVER_URL}/posts/${post.id}/likers`);
          if (likersResponse.ok) {
            const likersData = await likersResponse.json();
            setLikers({ ...likers, [post.id]: likersData.likers });
          }
        } catch (err) {
          console.error('Error reloading likers:', err);
        }
      }
    } catch (err) {
      console.error('Error updating like:', err);
      // Revert on error
      setLikedPosts(likedPosts);
      if (!err.message.includes('Already liked')) {
        alert(err.message);
      }
    }
  };

  const handleVouch = async post => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    const isVouched = vouchedPosts.has(post.id);
    const newVouchedPosts = new Set(vouchedPosts);

    if (isVouched) {
      newVouchedPosts.delete(post.id);
    } else {
      newVouchedPosts.add(post.id);
    }
    setVouchedPosts(newVouchedPosts);

    try {
      const url = `${SERVER_URL}/posts/${post.id}/vouch?user_email=${encodeURIComponent(user.email)}`;
      const response = await fetch(url, {
        method: isVouched ? 'DELETE' : 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update vouch');
      }

      const data = await response.json();

      // Update vouch count in profile
      setProfile(prev => ({
        ...prev,
        posts: prev.posts.map(p =>
          p.id === post.id ? { ...p, vouch_count: data.vouch_count } : p
        ),
      }));

      // Reload vouchers list if it's currently expanded
      if (vouchers[post.id] || expandedVouches.has(post.id)) {
        try {
          const vouchersResponse = await fetch(`${SERVER_URL}/posts/${post.id}/vouchers`);
          if (vouchersResponse.ok) {
            const vouchersData = await vouchersResponse.json();
            setVouchers({ ...vouchers, [post.id]: vouchersData.vouchers });
          }
        } catch (err) {
          console.error('Error reloading vouchers:', err);
        }
      }
    } catch (err) {
      console.error('Error updating vouch:', err);
      // Revert on error
      setVouchedPosts(vouchedPosts);
      if (!err.message.includes('Already vouched')) {
        alert(err.message);
      }
    }
  };

  const toggleDescription = postId => {
    const newExpanded = new Set(expandedDescriptions);
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
    }
    setExpandedDescriptions(newExpanded);
  };

  const handleAddComment = async postId => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }

    const commentText = newComment[postId];

    await addComment(
      postId,
      commentText,
      user.username,
      user.email,
      comments,
      setComments,
      newComment,
      setNewComment,
      // Callback to update comment count in profile posts
      postId => {
        setProfile(prev => ({
          ...prev,
          posts: prev.posts.map(post =>
            post.id === postId ? { ...post, comment_count: (post.comment_count || 0) + 1 } : post
          ),
        }));
        setCommentedPosts(prev => new Set([...prev, postId]));
      }
    );
  };

  const handleReplyClick = (postId, commentId, userName) => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    if (replyingTo[postId] === commentId) {
      setReplyingTo({ ...replyingTo, [postId]: null });
      setReplyText({ ...replyText, [postId]: '' });
    } else {
      setReplyingTo({ ...replyingTo, [postId]: commentId });
      setReplyText({ ...replyText, [postId]: `@${userName} ` });
    }
  };

  const handleAddReply = async (postId, parentId) => {
    const text = replyText[postId];
    if (!text || !text.trim()) return;

    await addComment(
      postId,
      text,
      user.username,
      user.email,
      comments,
      setComments,
      replyText,
      setReplyText,
      postId => {
        setProfile(prev => ({
          ...prev,
          posts: prev.posts.map(post =>
            post.id === postId ? { ...post, comment_count: (post.comment_count || 0) + 1 } : post
          ),
        }));
      },
      parentId
    );
    setReplyingTo({ ...replyingTo, [postId]: null });
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    await deleteComment(commentId, user.email, postId, comments, setComments, postId => {
      setProfile(prev => ({
        ...prev,
        posts: prev.posts.map(post =>
          post.id === postId ? { ...post, comment_count: Math.max((post.comment_count || 1) - 1, 0) } : post
        ),
      }));
    });
  };

  const handleEditComment = async (postId, commentId, content) => {
    if (!content || !content.trim()) return;
    await editComment(commentId, user.email, content, postId, comments, setComments);
  };

  const handleEditPost = async post => {
    setEditingPost(post);
    setEditFormData({
      title: post.title,
      description: post.description,
      category: post.category,
    });
    setEditImagesToDelete([]);
    setEditImages([]);
    setEditImageError('');
    // Fetch existing images
    try {
      const res = await fetch(`${SERVER_URL}/posts/${post.id}/images`);
      const fallback = post.image_url
        ? [{ type: 'existing', id: null, image_url: post.image_url, preview: post.image_url }]
        : [];
      if (res.ok) {
        const data = await res.json();
        setEditImages(
          data.images && data.images.length > 0
            ? data.images.map(img => ({ type: 'existing', id: img.id, image_url: img.image_url, preview: img.image_url }))
            : fallback
        );
      } else {
        setEditImages(fallback);
      }
    } catch {
      setEditImages(
        post.image_url ? [{ type: 'existing', id: null, image_url: post.image_url, preview: post.image_url }] : []
      );
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!isAuthenticated || !user || !editingPost) return;

    setSavingEdit(true);
    try {
      // 1. Delete removed existing images
      for (const imgId of editImagesToDelete) {
        if (imgId) {
          await fetch(`${SERVER_URL}/posts/${editingPost.id}/images/${imgId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }

      // 2. Upload new image files
      const newFiles = editImages.filter(i => i.type === 'new').map(i => i.file);
      const newImageUrls = newFiles.length > 0 ? await uploadImages(newFiles, token) : [];
      if (newImageUrls.length > 0) {
        await fetch(`${SERVER_URL}/posts/${editingPost.id}/images`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ image_urls: newImageUrls }),
        });
      }

      // 3. Persist display order (includes reordering existing + new)
      let newIdx = 0;
      const finalUrls = editImages.map(i => i.type === 'existing' ? i.image_url : newImageUrls[newIdx++]);
      if (finalUrls.length > 0) {
        await fetch(`${SERVER_URL}/posts/${editingPost.id}/images/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ image_urls: finalUrls }),
        });
      }

      // 3. Update post metadata
      const response = await fetch(`${SERVER_URL}/posts/${editingPost.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: user.email,
          title: editFormData.title,
          description: editFormData.description,
          category: editFormData.category,
          visibility: editingPost.visibility,
          status: editingPost.status,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update post');
      }

      const newPrimaryImage = finalUrls[0] ?? null;

      setProfile(prev => ({
        ...prev,
        posts: prev.posts.map(post =>
          post.id === editingPost.id
            ? { ...post, ...editFormData, image_url: newPrimaryImage }
            : post
        ),
      }));

      setEditSaved(true);
      setTimeout(() => {
        setShowEditModal(false);
        setEditingPost(null);
        setEditSaved(false);
      }, 1200);
    } catch (err) {
      console.error('Error updating post:', err);
      alert(err.message || 'Failed to update post. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePost = async () => {
    if (!isAuthenticated || !user || !editingPost) return;

    if (
      !window.confirm('Are you sure you want to delete this post? This action cannot be undone.')
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${SERVER_URL}/posts/${editingPost.id}?user_email=${encodeURIComponent(user.email)}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete post');
      }

      // Remove the post from the profile state
      setProfile(prev => ({
        ...prev,
        posts: prev.posts.filter(post => post.id !== editingPost.id),
      }));

      setShowEditModal(false);
      setEditingPost(null);
      alert('Post deleted successfully');
    } catch (err) {
      console.error('Error deleting post:', err);
      alert(err.message || 'Failed to delete post. Please try again.');
    }
  };

  const handleClosePost = async post => {
    if (!window.confirm('Close this post? It will remain visible but no longer accept gifts.')) return;
    try {
      const response = await fetch(`${SERVER_URL}/posts/${post.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'closed' }),
      });
      if (!response.ok) throw new Error('Failed to close post');
      setProfile(prev => ({
        ...prev,
        posts: prev.posts.map(p => p.id === post.id ? { ...p, status: 'closed' } : p),
      }));
    } catch (err) {
      console.error('Error closing post:', err);
      alert('Failed to close post. Please try again.');
    }
  };

  const handleReopenPost = async post => {
    try {
      const response = await fetch(`${SERVER_URL}/posts/${post.id}/reopen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to reopen post');
      setProfile(prev => ({
        ...prev,
        posts: prev.posts.map(p => p.id === post.id ? { ...p, status: 'active' } : p),
      }));
    } catch (err) {
      console.error('Error reopening post:', err);
      alert('Failed to reopen post. Please try again.');
    }
  };

  const handleShowPost = async postId => {
    setShowPostModal(true);
    setLoadingPost(true);
    try {
      const response = await fetch(`${SERVER_URL}/posts/${postId}`);
      const data = await response.json();
      setSelectedPost(data);
    } catch (err) {
      console.error('Error loading post:', err);
    } finally {
      setLoadingPost(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto mt-10 text-center">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto mt-10">
        <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded">
          <h2 className="font-bold mb-2">Error</h2>
          <p>{error}</p>
        </div>
        <Link to="/" className="mt-4 inline-block text-indigo-600 hover:underline">
          ← Back to Feed
        </Link>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto mt-10">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 p-4 rounded">
          <h2 className="font-bold mb-2">Profile Not Found</h2>
          <p>
            This user profile could not be loaded. They may not have completed their account setup
            yet.
          </p>
        </div>
        <Link to="/" className="mt-4 inline-block text-indigo-600 hover:underline">
          ← Back to Feed
        </Link>
      </div>
    );
  }

  if (profile.is_private) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Use shared Navigation component */}
        <Navigation />
        <div className="max-w-4xl mx-auto mt-10 px-4">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
            >
              ← Back to Feed
            </Link>
          </div>
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-8">
            <h1 className="text-3xl font-bold mb-4 text-gray-800">🔒 {profile.username}</h1>
            <p className="text-gray-600 text-lg">{profile.message}</p>
          </div>
        </div>
      </div>
    );
  }

  // Create unified timeline
  const timelineItems = [];

  // Add gifts given
  if (profile.gifts_given) {
    profile.gifts_given.forEach(gift => {
      timelineItems.push({
        type: 'gift_given',
        date: gift.created_at,
        data: gift,
      });
    });
  }

  // Add gifts received
  if (profile.gifts_received) {
    profile.gifts_received.forEach(gift => {
      timelineItems.push({
        type: 'gift_received',
        date: gift.claimed_at || gift.created_at,
        data: gift,
      });
    });
  }

  // Add posts
  if (profile.posts) {
    profile.posts.forEach(post => {
      timelineItems.push({
        type: 'post',
        // Use shared_at for shared posts, created_at for original posts
        date: post.shared_at || post.created_at,
        data: post.is_shared
          ? { ...post, sharer_name: profile.username, sharer_id: userId }
          : post,
      });
    });
  }

  // Sort pool posts with open pools first, then by date
  const poolItems = timelineItems
    .filter(item => item.type === 'post')
    .sort((a, b) => {
      const aIsOpenPool =
        a.data.goal_amount &&
        a.data.current_amount < a.data.goal_amount &&
        a.data.status === 'active';
      const bIsOpenPool =
        b.data.goal_amount &&
        b.data.current_amount < b.data.goal_amount &&
        b.data.status === 'active';
      if (aIsOpenPool && !bIsOpenPool) return -1;
      if (!aIsOpenPool && bIsOpenPool) return 1;
      return new Date(b.date) - new Date(a.date);
    });

  // Sort gift events by date (most recent first)
  const activityItems = timelineItems
    .filter(item => item.type !== 'post')
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const formatDateTime = isoString => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Use shared Navigation component */}
      <Navigation />

      <div className="max-w-6xl mx-auto mt-8 px-4 sm:px-6 lg:px-8 pb-12 flex gap-6">
        {/* Invisible sidebar placeholder to align with feed layout */}
        <div className="hidden lg:block w-64 flex-shrink-0" />
        <div className="flex-1 max-w-2xl min-w-0">
        {/* Profile Header */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
            {/* Left Side: Name & Social */}
            <div className="space-y-3 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-4 mb-2">
                {isAuthenticated && user && profile.email === user.email ? (
                  <ProfilePhotoEditor
                    onSuccess={url => setProfile(p => ({ ...p, profile_photo_url: url }))}
                    currentPhotoUrl={profile.profile_photo_url}
                  >
                    {({ openModal }) => (
                      <div className="relative cursor-pointer group" onClick={openModal}>
                        <Avatar
                          name={profile.username}
                          email={profile.email}
                          profilePhotoUrl={profile.profile_photo_url}
                          size="w-16 h-16 sm:w-20 sm:h-20"
                          textSize="text-2xl sm:text-3xl"
                        />
                        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                            <path d="M12 9a3.75 3.75 0 1 0 0 7.5A3.75 3.75 0 0 0 12 9Z" />
                            <path fillRule="evenodd" d="M9.344 3.071a49.52 49.52 0 0 1 5.312 0c.967.052 1.83.585 2.332 1.39l.821 1.317c.24.383.645.643 1.11.71.386.054.77.113 1.152.177 1.432.239 2.429 1.493 2.429 2.909V18a3 3 0 0 1-3 3h-15a3 3 0 0 1-3-3V9.574c0-1.416.997-2.67 2.429-2.909.382-.064.766-.123 1.151-.178a1.56 1.56 0 0 0 1.11-.71l.822-1.315a2.942 2.942 0 0 1 2.332-1.39ZM6.75 12.75a5.25 5.25 0 1 1 10.5 0 5.25 5.25 0 0 1-10.5 0Zm12-1.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </ProfilePhotoEditor>
                ) : (
                  <Avatar
                    name={profile.username}
                    email={profile.email}
                    profilePhotoUrl={profile.profile_photo_url}
                    size="w-16 h-16 sm:w-20 sm:h-20"
                    textSize="text-2xl sm:text-3xl"
                  />
                )}
                <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight break-words">
                  {profile.username}
                </h1>
              </div>
              <div className="flex items-center justify-center sm:justify-start gap-4 text-sm text-gray-500">
                <button
                  onClick={handleShowFollowers}
                  className="hover:text-indigo-600 transition-colors cursor-pointer group"
                >
                  <span className="font-bold text-gray-900 group-hover:text-indigo-600">
                    {profile.followers_count || 0}
                  </span>{' '}
                  {profile.followers_count === 1 ? 'Follower' : 'Followers'}
                </button>
                <div className="w-1 h-1 bg-gray-300 rounded-full" />
                <button
                  onClick={handleShowFollowing}
                  className="hover:text-indigo-600 transition-colors cursor-pointer group"
                >
                  <span className="font-bold text-gray-900 group-hover:text-indigo-600">
                    {profile.following_count || 0}
                  </span>{' '}
                  Following
                </button>
              </div>
            </div>

            {/* Right Side: Action Buttons */}
            {isAuthenticated && user && user.id !== userId && followStatusLoaded && (
              <div className="flex flex-col items-stretch gap-2 w-full sm:w-auto sm:min-w-[200px]">
                <button
                  onClick={handleFollowToggle}
                  disabled={followLoading}
                  className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95 whitespace-nowrap text-center ${
                    isFollowing
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-indigo-500 text-white shadow-md shadow-indigo-100 hover:bg-indigo-600'
                  } disabled:opacity-50`}
                >
                  {followLoading ? '...' : isFollowing ? 'Following' : 'Follow'}
                </button>
                <Link
                  to={`/send?recipient=${encodeURIComponent(profile.username)}&email=${encodeURIComponent(profile.email)}`}
                  state={{ background: location }}
                  className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl font-bold text-sm shadow-md shadow-emerald-100 hover:bg-emerald-600 transition-all active:scale-95 whitespace-nowrap overflow-hidden text-ellipsis text-center"
                >
                  💝 Gift {profile.username.split(' ')[0]}
                </Link>
              </div>
            )}
          </div>

          {/* Stats Grid */}
          {privacySettings.show_gift_stats && (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {/* Gifts Given */}
              <div className="bg-emerald-50/50 border border-emerald-100 p-4 sm:p-5 rounded-2xl text-center sm:text-left">
                <p className="text-[10px] sm:text-xs font-bold text-emerald-600 uppercase tracking-widest mb-1">
                  Gifts Given
                </p>
                <p className="text-xl sm:text-2xl font-black text-emerald-900">
                  $
                  {profile.total_given.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>

              {/* Gifts Received */}
              <div className="bg-indigo-50/50 border border-indigo-100 p-4 sm:p-5 rounded-2xl text-center sm:text-left">
                <p className="text-[10px] sm:text-xs font-bold text-indigo-600 uppercase tracking-widest mb-1">
                  Gifts Received
                </p>
                <p className="text-xl sm:text-2xl font-black text-indigo-900">
                  $
                  {profile.total_received.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* No activity at all */}
        {poolItems.length === 0 && activityItems.length === 0 && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-4">💝</div>
            <p className="text-gray-600 text-lg">No activity yet</p>
          </div>
        )}

        {/* Tabs */}
        {(poolItems.length > 0 || activityItems.length > 0) && (() => {
          const effectiveTab = activeTab === 'pools' && poolItems.length === 0 ? 'activity'
            : activeTab;
          return (
            <>
              <div className="flex mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setActiveTab('pools')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                    effectiveTab === 'pools'
                      ? 'text-indigo-600 bg-white border-b-2 border-indigo-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Gift Pools
                  {poolItems.length > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${effectiveTab === 'pools' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                      {poolItems.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('activity')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                    effectiveTab === 'activity'
                      ? 'text-indigo-600 bg-white border-b-2 border-indigo-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Gifting History
                  {activityItems.length > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${effectiveTab === 'activity' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                      {activityItems.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Gift Pools Tab */}
              {effectiveTab === 'pools' && (
                poolItems.length > 0 ? (
                  <div className="space-y-4">
                    {poolItems.map((item, index) => (
                      <GiftPoolPost
                        key={index}
                        post={item.data}
                        user={user}
                        isAuthenticated={isAuthenticated}
                        isProfilePage
                        likedPosts={likedPosts}
                        sharedPosts={new Set()}
                        vouchedPosts={vouchedPosts}
                        commentedPosts={commentedPosts}
                        expandedDonors={expandedDonors}
                        expandedLikes={expandedLikes}
                        expandedComments={expandedComments}
                        expandedDescriptions={expandedDescriptions}
                        expandedVouches={expandedVouches}
                        donors={donors}
                        likers={likers}
                        comments={comments}
                        newComment={newComment}
                        vouchers={vouchers}
                        onLike={handleLike}
                        onShare={null}
                        onVouch={handleVouch}
                        onToggleDonors={toggleDonors}
                        onToggleLikes={toggleLikes}
                        onToggleComments={toggleComments}
                        onToggleDescription={toggleDescription}
                        onToggleVouches={toggleVouches}
                        onAddComment={handleAddComment}
                        onCommentChange={(postId, value) =>
                          setNewComment({ ...newComment, [postId]: value })
                        }
                        replyingTo={replyingTo}
                        replyText={replyText}
                        onReplyClick={handleReplyClick}
                        onReplyTextChange={(postId, value) =>
                          setReplyText({ ...replyText, [postId]: value })
                        }
                        onAddReply={handleAddReply}
                        onDeleteComment={handleDeleteComment}
                        onEditComment={handleEditComment}
                        highlightedPostId={highlightedPostId}
                        onEditPost={handleEditPost}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                    <p className="text-gray-500 text-sm">No gift pools yet</p>
                  </div>
                )
              )}

              {/* Activity Tab */}
              {effectiveTab === 'activity' && (
                activityItems.length > 0 ? (
                  <div className="space-y-0">
              {activityItems.map((item, index) => {
                const isLast = index === activityItems.length - 1;
                const isPoolContribution = item.type === 'gift_received'
                  ? item.data.message?.startsWith('Supporting:')
                  : !!item.data.post;
                const poolTitle = item.type === 'gift_received' && isPoolContribution
                  ? item.data.message.replace(/^Supporting:\s*(\[Testing\]\s*)?/, '').trim()
                  : null;

                if (item.type === 'gift_given') {
                  return (
                    <div key={index} id={`post-${item.data.id}`} className="flex gap-3">
                      <div className="flex flex-col items-center w-6 flex-shrink-0">
                        <div className="w-3 h-3 rounded-full mt-4 flex-shrink-0 bg-emerald-100 border-2 border-emerald-400 z-10" />
                        {!isLast && <div className="w-px flex-1 bg-gray-100 mt-1" />}
                      </div>
                      <div className="flex-1 min-w-0 pb-3">
                        <div
                          className={`bg-white rounded-xl border transition-all duration-300 px-4 py-3 shadow-sm ${
                            highlightedPostId === item.data.id
                              ? 'ring-2 ring-indigo-500 border-indigo-300'
                              : 'border-gray-100 hover:border-gray-200 hover:shadow'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap text-sm">
                                {isAuthenticated && user && profile.email === user.email && (
                                  <span className="font-semibold text-gray-900">You</span>
                                )}
                                <span className="text-gray-500">
                                  {isPoolContribution ? 'contributed to' : 'gifted'}
                                </span>
                                {item.data.recipient_id || item.data.recipient_email ? (
                                  <Link
                                    to={`/profile/${item.data.recipient_id || item.data.recipient_email}`}
                                    className="font-semibold text-gray-900 hover:text-emerald-600 hover:underline"
                                  >
                                    {item.data.recipient_name}{isPoolContribution ? "'s" : ''}
                                  </Link>
                                ) : (
                                  <span className="font-semibold text-gray-900">
                                    {item.data.recipient_name}{isPoolContribution ? "'s" : ''}
                                  </span>
                                )}
                                {isPoolContribution && (
                                  <>
                                    <span className="text-gray-500">pool</span>
                                    <button
                                      onClick={() => handleShowPost(item.data.post.id)}
                                      className="font-medium text-gray-700 hover:text-emerald-600 hover:underline"
                                    >
                                      {item.data.post.title}
                                    </button>
                                  </>
                                )}
                                <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  {item.data.occasion}
                                </span>
                              </div>
                              {item.data.message && !item.data.message.startsWith('Supporting:') && (
                                <p className="text-xs italic text-gray-500 mt-1">
                                  "{item.data.message}"
                                </p>
                              )}
                            </div>
                            <div className="flex-shrink-0 text-right">
                              {(privacySettings.show_gift_amounts || (isAuthenticated && user && profile.email === user.email)) && (
                                <span className="flex items-center justify-end gap-1">
                                  <span className="text-base font-bold text-emerald-600">
                                    ${item.data.amount.toFixed(2)}
                                  </span>
                                  {!privacySettings.show_gift_amounts && isAuthenticated && user && profile.email === user.email && (
                                    <span title="Hidden from others" className="text-gray-400">
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                        <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l14.5 14.5a.75.75 0 1 0 1.06-1.06l-1.745-1.745a10.029 10.029 0 0 0 3.3-4.38 1.651 1.651 0 0 0 0-1.185A10.004 10.004 0 0 0 9.999 3a9.956 9.956 0 0 0-4.744 1.194L3.28 2.22ZM7.752 6.69l1.092 1.092a2.5 2.5 0 0 1 3.374 3.373l1.091 1.092a4 4 0 0 0-5.557-5.557Z" clipRule="evenodd" />
                                        <path d="M10.748 13.93l2.523 2.523a9.987 9.987 0 0 1-3.27.547c-4.258 0-7.894-2.66-9.337-6.41a1.651 1.651 0 0 1 0-1.186A10.007 10.007 0 0 1 2.839 6.02L6.07 9.252a4 4 0 0 0 4.678 4.678Z" />
                                      </svg>
                                    </span>
                                  )}
                                </span>
                              )}
                              <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(item.date)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                } else if (item.type === 'gift_received') {
                  return (
                    <div key={index} id={`post-${item.data.id}`} className="flex gap-3">
                      <div className="flex flex-col items-center w-6 flex-shrink-0">
                        <div className="w-3 h-3 rounded-full mt-4 flex-shrink-0 bg-indigo-100 border-2 border-indigo-400 z-10" />
                        {!isLast && <div className="w-px flex-1 bg-gray-100 mt-1" />}
                      </div>
                      <div className="flex-1 min-w-0 pb-3">
                        <div
                          className={`bg-white rounded-xl border transition-all duration-300 px-4 py-3 shadow-sm ${
                            highlightedPostId === item.data.id
                              ? 'ring-2 ring-indigo-500 border-indigo-300'
                              : 'border-gray-100 hover:border-gray-200 hover:shadow'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap text-sm">
                                {item.data.giver_id ? (
                                  <Link
                                    to={`/profile/${item.data.giver_id}`}
                                    className="font-semibold text-gray-900 hover:text-indigo-600 hover:underline"
                                  >
                                    {item.data.giver_name}
                                  </Link>
                                ) : (
                                  <span className="font-semibold text-gray-900">
                                    {item.data.giver_name}
                                  </span>
                                )}
                                <span className="text-gray-500">
                                  {isPoolContribution ? 'contributed to' : 'gifted'}
                                </span>
                                {!isPoolContribution && isAuthenticated && user && profile.email === user.email && (
                                  <span className="font-semibold text-gray-900">you</span>
                                )}
                                {isPoolContribution && (
                                  <>
                                    {isAuthenticated && user && profile.email === user.email && (
                                      <span className="text-gray-500">your pool</span>
                                    )}
                                    {poolTitle && (
                                      <span className="font-medium text-gray-700">{poolTitle}</span>
                                    )}
                                  </>
                                )}
                                <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  {item.data.occasion}
                                </span>
                              </div>
                              {item.data.message && !item.data.message.startsWith('Supporting:') && (
                                <p className="text-xs italic text-gray-500 mt-1">
                                  "{item.data.message}"
                                </p>
                              )}
                            </div>
                            <div className="flex-shrink-0 text-right">
                              {(privacySettings.show_gift_amounts || (isAuthenticated && user && profile.email === user.email)) && (
                                <span className="flex items-center justify-end gap-1">
                                  <span className="text-base font-bold text-indigo-600">
                                    ${item.data.amount.toFixed(2)}
                                  </span>
                                  {!privacySettings.show_gift_amounts && isAuthenticated && user && profile.email === user.email && (
                                    <span title="Hidden from others" className="text-gray-400">
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                        <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l14.5 14.5a.75.75 0 1 0 1.06-1.06l-1.745-1.745a10.029 10.029 0 0 0 3.3-4.38 1.651 1.651 0 0 0 0-1.185A10.004 10.004 0 0 0 9.999 3a9.956 9.956 0 0 0-4.744 1.194L3.28 2.22ZM7.752 6.69l1.092 1.092a2.5 2.5 0 0 1 3.374 3.373l1.091 1.092a4 4 0 0 0-5.557-5.557Z" clipRule="evenodd" />
                                        <path d="M10.748 13.93l2.523 2.523a9.987 9.987 0 0 1-3.27.547c-4.258 0-7.894-2.66-9.337-6.41a1.651 1.651 0 0 1 0-1.186A10.007 10.007 0 0 1 2.839 6.02L6.07 9.252a4 4 0 0 0 4.678 4.678Z" />
                                      </svg>
                                    </span>
                                  )}
                                </span>
                              )}
                              <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(item.date)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                    <p className="text-gray-500 text-sm">No activity yet</p>
                  </div>
                )
              )}
            </>
          );
        })()}

        {/* Followers Modal */}
        {showFollowersModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Followers</h2>
                <button
                  onClick={() => setShowFollowersModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto max-h-[60vh] p-6">
                {loadingFollowers ? (
                  <p className="text-center text-gray-500">Loading...</p>
                ) : followersList.length > 0 ? (
                  <div className="space-y-3">
                    {followersList.map(follower => (
                      <Link
                        key={follower.id}
                        to={`/profile/${follower.id}`}
                        onClick={() => setShowFollowersModal(false)}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Avatar
                          name={follower.username}
                          email={follower.email}
                          profilePhotoUrl={follower.profile_photo_url}
                          size="w-12 h-12"
                          textSize="text-lg"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{follower.username}</p>
                          <p className="text-sm text-gray-500">{follower.email}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No followers yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Following Modal */}
        {showFollowingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Following</h2>
                <button
                  onClick={() => setShowFollowingModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="overflow-y-auto max-h-[60vh] p-6">
                {loadingFollowing ? (
                  <p className="text-center text-gray-500">Loading...</p>
                ) : followingList.length > 0 ? (
                  <div className="space-y-3">
                    {followingList.map(following => (
                      <Link
                        key={following.id}
                        to={`/profile/${following.id}`}
                        onClick={() => setShowFollowingModal(false)}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Avatar
                          name={following.username}
                          email={following.email}
                          profilePhotoUrl={following.profile_photo_url}
                          size="w-12 h-12"
                          textSize="text-lg"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{following.username}</p>
                          <p className="text-sm text-gray-500">{following.email}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Not following anyone yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Post Details Modal */}
        {showPostModal && selectedPost && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-y-auto relative">
              <button
                onClick={() => setShowPostModal(false)}
                className="sticky top-2 right-2 float-right z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ×
              </button>
              <div className="clear-both">
                {loadingPost ? (
                  <p className="text-center text-gray-500 p-6">Loading...</p>
                ) : (
                  <GiftPoolPost
                    post={selectedPost}
                    user={user}
                    isAuthenticated={isAuthenticated}
                    isProfilePage
                    likedPosts={likedPosts}
                    sharedPosts={new Set()}
                    vouchedPosts={vouchedPosts}
                    expandedDonors={expandedDonors}
                    expandedLikes={expandedLikes}
                    expandedComments={expandedComments}
                    expandedDescriptions={expandedDescriptions}
                    expandedVouches={expandedVouches}
                    donors={donors}
                    likers={likers}
                    comments={comments}
                    newComment={newComment}
                    vouchers={vouchers}
                    onLike={handleLike}
                    onShare={null}
                    onVouch={handleVouch}
                    onToggleDonors={toggleDonors}
                    onToggleLikes={toggleLikes}
                    onToggleComments={toggleComments}
                    onToggleDescription={toggleDescription}
                    onToggleVouches={toggleVouches}
                    onAddComment={handleAddComment}
                    onCommentChange={(postId, value) =>
                      setNewComment({ ...newComment, [postId]: value })
                    }
                    replyingTo={replyingTo}
                    replyText={replyText}
                    onReplyClick={handleReplyClick}
                    onReplyTextChange={(postId, value) =>
                      setReplyText({ ...replyText, [postId]: value })
                    }
                    onAddReply={handleAddReply}
                    onDeleteComment={handleDeleteComment}
                    onEditComment={handleEditComment}
                    highlightedPostId={null}
                    onEditPost={handleEditPost}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Post Modal */}
        {showEditModal && editingPost && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-2xl font-bold text-gray-900">Edit Post</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPost(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="overflow-y-auto flex-1 min-h-0 p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={editFormData.title}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        title: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Post title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        description: e.target.value,
                      })
                    }
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    placeholder="Post description"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={editFormData.category}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        category: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select a category...</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Photo Management */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Photos</label>
                    <span className="text-xs text-gray-400">{editImages.length}/5 max</span>
                  </div>

                  {/* Image grid */}
                  {editImages.length > 0 && (
                    <div className="mb-3">
                      <SortableImageGrid
                        items={editImages}
                        onReorder={(from, to) => {
                          setEditImages(prev => {
                            const arr = [...prev];
                            const [moved] = arr.splice(from, 1);
                            arr.splice(to, 0, moved);
                            return arr;
                          });
                        }}
                        onRemove={index => {
                          const item = editImages[index];
                          if (item.type === 'existing' && item.id) {
                            setEditImagesToDelete(prev => [...prev, item.id]);
                          }
                          setEditImages(prev => prev.filter((_, i) => i !== index));
                        }}
                      />
                    </div>
                  )}

                  {/* Add photos button */}
                  {editImages.length < 5 && (
                    <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors w-fit">
                      <span className="text-gray-500 text-sm">+ Add photos</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        multiple
                        className="hidden"
                        onChange={e => {
                          const files = Array.from(e.target.files);
                          const remaining = 5 - editImages.length;
                          const toAdd = files.slice(0, remaining);
                          const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                          const valid = toAdd.filter(f => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024);
                          setEditImageError(valid.length < toAdd.length ? 'Some files were skipped (invalid type or over 5MB).' : '');
                          if (valid.length === 0) return;
                          valid.forEach(file => {
                            const reader = new FileReader();
                            reader.onloadend = () =>
                              setEditImages(prev => [...prev, { type: 'new', file, preview: reader.result }]);
                            reader.readAsDataURL(file);
                          });
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}

                  {editImageError && (
                    <p className="mt-1 text-xs text-red-600">{editImageError}</p>
                  )}
                </div>

                {/* Warning if post has contributions */}
                {editingPost.current_amount > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <p className="text-sm text-indigo-800">
                      ⚠️ This post has received contributions. You can only edit the title,
                      description, category, and photos.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 p-6 space-y-3 flex-shrink-0">
                {/* Save/Cancel buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingPost(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={savingEdit || editSaved}
                    className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editSaved ? 'Saved!' : savingEdit ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>

                {/* Close/Reopen + Delete row */}
                <div className="flex gap-3">
                  {editingPost.status === 'closed' ? (
                    <button
                      onClick={() => { handleReopenPost(editingPost); setShowEditModal(false); setEditingPost(null); }}
                      className="flex-1 px-4 py-2 bg-green-50 text-green-700 rounded-lg font-medium hover:bg-green-100 transition-colors border border-green-200"
                    >
                      🔓 Reopen Post
                    </button>
                  ) : (
                    <div className="relative group flex-1">
                      <button
                        onClick={() => { handleClosePost(editingPost); setShowEditModal(false); setEditingPost(null); }}
                        className="w-full px-4 py-2 bg-gray-50 text-gray-600 rounded-lg font-medium hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        🔒 Close Post
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 px-2 py-1.5 bg-gray-800 text-white text-xs rounded text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Closing a post removes it from the feed and stops it from accepting gifts, but keeps it visible on your profile. You can reopen it any time.
                      </div>
                    </div>
                  )}
                  {editingPost.current_amount === 0 ? (
                    <div className="relative group flex-1">
                      <button
                        onClick={handleDeletePost}
                        className="w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors border border-red-200"
                      >
                        🗑️ Delete Post
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 px-2 py-1.5 bg-gray-800 text-white text-xs rounded text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Permanently delete this post. This cannot be undone.
                      </div>
                    </div>
                  ) : (
                    <div className="relative group flex-1">
                      <button
                        disabled
                        className="w-full px-4 py-2 bg-red-50 text-red-300 rounded-lg font-medium border border-red-100 cursor-not-allowed"
                      >
                        🗑️ Delete Post
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 px-2 py-1.5 bg-gray-800 text-white text-xs rounded text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Cannot delete posts that have received contributions.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
